// src/OwnerPortal.js
import React, { useState, useEffect } from 'react';
import { auth, googleProvider, db } from './firebase';
import { signInWithPopup, signOut } from 'firebase/auth';
import { collection, query, where, getDocs, updateDoc, doc } from 'firebase/firestore';
import Footer from './Footer';

const OwnerPortal = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [myStations, setMyStations] = useState([]);
  const [showClaimForm, setShowClaimForm] = useState(false);
  const [claimCode, setClaimCode] = useState('');
  const [editingStation, setEditingStation] = useState(null);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((currentUser) => {
      setUser(currentUser);
      setLoading(false);
      if (currentUser) {
        loadMyStations(currentUser.uid);
      }
    });
    return () => unsubscribe();
  }, []);

  const loadMyStations = async (userId) => {
    try {
      const q = query(collection(db, 'stations'), where('claimedBy', '==', userId));
      const querySnapshot = await getDocs(q);
      const stations = [];
      querySnapshot.forEach((doc) => {
        stations.push({ id: doc.id, ...doc.data() });
      });
      setMyStations(stations);
    } catch (error) {
      console.error('Error loading stations:', error);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (error) {
      console.error('Sign in error:', error);
      alert('Sign in failed. Please try again.');
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      setMyStations([]);
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const handleClaimStation = async () => {
    if (!claimCode.trim()) {
      alert('Please enter an owner code');
      return;
    }

    try {
      // Find station with this owner code
      const q = query(collection(db, 'stations'), where('ownerCode', '==', claimCode.toUpperCase()));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        alert('❌ Invalid owner code. Please check and try again.');
        return;
      }

      // Check if already claimed
      const stationDoc = querySnapshot.docs[0];
      const stationData = stationDoc.data();

      if (stationData.claimedBy && stationData.claimedBy !== user.uid) {
        alert('❌ This station has already been claimed by another owner.');
        return;
      }

      // Claim the station
      await updateDoc(doc(db, 'stations', stationDoc.id), {
        claimedBy: user.uid,
        claimedByEmail: user.email,
        claimedByName: user.displayName,
        claimedAt: new Date().toISOString()
      });

      setClaimCode('');
      setShowClaimForm(false);
      await loadMyStations(user.uid);
      alert('✅ Station claimed successfully! You can now manage it.');
    } catch (error) {
      console.error('Error claiming station:', error);
      alert('❌ Failed to claim station. Please try again.');
    }
  };

  const handleUpdateStation = async (stationId, updates) => {
    try {
      await updateDoc(doc(db, 'stations', stationId), {
        ...updates,
        lastUpdated: new Date().toISOString(),
        updatedBy: user.displayName || user.email,
        updatedByOwner: true // Mark as owner update - overrides public updates
      });
      await loadMyStations(user.uid);
      setEditingStation(null);
      alert('✅ Station updated successfully!');
    } catch (error) {
      console.error('Error updating station:', error);
      alert('❌ Failed to update station. Please try again.');
    }
  };

  const EditStationModal = ({ station, onClose }) => {
    const [formData, setFormData] = useState({
      prices: { ...station.prices },
      availability: { ...station.availability },
      queue: station.queue
    });

    const updateField = (field, value) => {
      if (field.includes('.')) {
        const [parent, child] = field.split('.');
        setFormData({
          ...formData,
          [parent]: {
            ...formData[parent],
            [child]: value
          }
        });
      } else {
        setFormData({ ...formData, [field]: value });
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
        <div className="bg-white rounded-lg p-6 max-w-2xl w-full my-8 max-h-[90vh] overflow-y-auto">
          <h3 className="text-2xl font-bold mb-4">Update Your Station</h3>
          <p className="text-gray-600 mb-6">{station.name}</p>

          <div className="space-y-6">
            <div className="border-t pt-4">
              <h4 className="font-medium mb-3">Fuel Prices</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">PMS - Petrol (₦/L)</label>
                  <input
                    type="number"
                    value={formData.prices.PMS}
                    onChange={(e) => updateField('prices.PMS', parseInt(e.target.value))}
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">AGO - Diesel (₦/L)</label>
                  <input
                    type="number"
                    value={formData.prices.AGO}
                    onChange={(e) => updateField('prices.AGO', parseInt(e.target.value))}
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">DPK - Kerosene (₦/L)</label>
                  <input
                    type="number"
                    value={formData.prices.DPK}
                    onChange={(e) => updateField('prices.DPK', parseInt(e.target.value))}
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">LPG - Cooking Gas (₦/kg)</label>
                  <input
                    type="number"
                    value={formData.prices.LPG}
                    onChange={(e) => updateField('prices.LPG', parseInt(e.target.value))}
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>
              </div>
            </div>

            <div className="border-t pt-4">
              <h4 className="font-medium mb-3">Availability Status</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">PMS</label>
                  <select
                    value={formData.availability.PMS}
                    onChange={(e) => updateField('availability.PMS', e.target.value)}
                    className="w-full border rounded-lg px-3 py-2"
                  >
                    <option value="available">Available</option>
                    <option value="limited">Limited</option>
                    <option value="out">Out of Stock</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">AGO</label>
                  <select
                    value={formData.availability.AGO}
                    onChange={(e) => updateField('availability.AGO', e.target.value)}
                    className="w-full border rounded-lg px-3 py-2"
                  >
                    <option value="available">Available</option>
                    <option value="limited">Limited</option>
                    <option value="out">Out of Stock</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">DPK</label>
                  <select
                    value={formData.availability.DPK}
                    onChange={(e) => updateField('availability.DPK', e.target.value)}
                    className="w-full border rounded-lg px-3 py-2"
                  >
                    <option value="available">Available</option>
                    <option value="limited">Limited</option>
                    <option value="out">Out of Stock</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">LPG</label>
                  <select
                    value={formData.availability.LPG}
                    onChange={(e) => updateField('availability.LPG', e.target.value)}
                    className="w-full border rounded-lg px-3 py-2"
                  >
                    <option value="available">Available</option>
                    <option value="limited">Limited</option>
                    <option value="out">Out of Stock</option>
                  </select>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Current Queue Status</label>
              <select
                value={formData.queue}
                onChange={(e) => updateField('queue', e.target.value)}
                className="w-full border rounded-lg px-3 py-2"
              >
                <option value="none">No Queue</option>
                <option value="short">Short Queue (&lt;5 mins)</option>
                <option value="moderate">Moderate Queue (5-15 mins)</option>
                <option value="long">Long Queue (&gt;15 mins)</option>
              </select>
            </div>

            <div className="flex gap-3 pt-4 border-t">
              <button
                onClick={() => handleUpdateStation(station.id, formData)}
                className="flex-1 bg-green-600 text-white py-3 rounded-lg font-medium hover:bg-green-700"
              >
                Save Changes
              </button>
              <button
                onClick={onClose}
                className="flex-1 bg-gray-200 text-gray-800 py-3 rounded-lg font-medium hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-600 to-green-800">
        <div className="bg-white rounded-lg shadow-2xl p-8 max-w-md w-full">
          <div className="text-center mb-8">
            <div className="text-5xl mb-4">⛽</div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Station Owner Portal</h1>
            <p className="text-gray-600">Manage your filling station</p>
          </div>
          
          <button
            onClick={handleGoogleSignIn}
            className="w-full bg-white border-2 border-gray-300 text-gray-700 py-3 px-4 rounded-lg font-medium hover:bg-gray-50 flex items-center justify-center gap-3 transition-all"
          >
            <svg className="w-6 h-6" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Sign in with Google
          </button>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Station owners: Sign in to claim and manage your stations
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Station Owner Portal</h1>
            <p className="text-sm text-gray-600">Manage your filling stations</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-sm font-medium text-gray-900">{user.displayName}</p>
              <p className="text-xs text-gray-600">{user.email}</p>
            </div>
            <button
              onClick={handleSignOut}
              className="bg-red-600 text-white py-2 px-4 rounded-lg text-sm hover:bg-red-700"
            >
              Sign Out
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats & Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-sm text-gray-600 mb-2">My Stations</div>
            <div className="text-3xl font-bold text-gray-900">{myStations.length}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-6 md:col-span-2">
            <button
              onClick={() => setShowClaimForm(!showClaimForm)}
              className="w-full bg-green-600 text-white py-3 rounded-lg font-medium hover:bg-green-700 flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Claim a Station
            </button>
          </div>
        </div>

        {/* Claim Form */}
        {showClaimForm && (
          <div className="bg-white rounded-lg shadow p-6 mb-8">
            <h3 className="text-lg font-bold mb-4">Claim Your Station</h3>
            <p className="text-sm text-gray-600 mb-4">
              Enter the owner code provided by the administrator to claim your station.
            </p>
            <div className="flex gap-3">
              <input
                type="text"
                value={claimCode}
                onChange={(e) => setClaimCode(e.target.value.toUpperCase())}
                placeholder="Enter 8-character code (e.g., ABC12XYZ)"
                maxLength={8}
                className="flex-1 border rounded-lg px-4 py-3 uppercase tracking-wider"
              />
              <button
                onClick={handleClaimStation}
                className="bg-green-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-green-700"
              >
                Claim Station
              </button>
              <button
                onClick={() => {
                  setShowClaimForm(false);
                  setClaimCode('');
                }}
                className="bg-gray-200 text-gray-800 px-6 py-3 rounded-lg font-medium hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* My Stations */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b">
            <h2 className="text-xl font-bold text-gray-900">Your Stations</h2>
          </div>

          {myStations.length === 0 ? (
            <div className="p-12 text-center">
              <div className="text-6xl mb-4">⛽</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No stations claimed yet</h3>
              <p className="text-gray-600 mb-6">
                Click "Claim a Station" above and enter your owner code to get started.
              </p>
            </div>
          ) : (
            <div className="divide-y">
              {myStations.map((station) => (
                <div key={station.id} className="p-6 hover:bg-gray-50">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">{station.name}</h3>
                      <p className="text-sm text-gray-600">{station.address}, {station.city}</p>
                      <p className="text-xs text-gray-500 mt-1">{station.state}</p>
                    </div>
                    <button
                      onClick={() => setEditingStation(station)}
                      className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700"
                    >
                      Update Prices
                    </button>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-gray-50 rounded-lg p-3">
                      <div className="text-xs text-gray-600 mb-1">PMS (Petrol)</div>
                      <div className="text-lg font-bold">₦{station.prices?.PMS || 0}</div>
                      <div className={`text-xs mt-1 ${station.availability?.PMS === 'available' ? 'text-green-600' : 'text-red-600'}`}>
                        {station.availability?.PMS || 'available'}
                      </div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <div className="text-xs text-gray-600 mb-1">AGO (Diesel)</div>
                      <div className="text-lg font-bold">₦{station.prices?.AGO || 0}</div>
                      <div className={`text-xs mt-1 ${station.availability?.AGO === 'available' ? 'text-green-600' : 'text-red-600'}`}>
                        {station.availability?.AGO || 'available'}
                      </div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <div className="text-xs text-gray-600 mb-1">DPK (Kerosene)</div>
                      <div className="text-lg font-bold">₦{station.prices?.DPK || 0}</div>
                      <div className={`text-xs mt-1 ${station.availability?.DPK === 'available' ? 'text-green-600' : 'text-red-600'}`}>
                        {station.availability?.DPK || 'available'}
                      </div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <div className="text-xs text-gray-600 mb-1">LPG (Cooking Gas)</div>
                      <div className="text-lg font-bold">₦{station.prices?.LPG || 0}/kg</div>
                      <div className={`text-xs mt-1 ${station.availability?.LPG === 'available' ? 'text-green-600' : 'text-red-600'}`}>
                        {station.availability?.LPG || 'available'}
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 flex items-center justify-between text-xs text-gray-500">
                    <span>Last updated: {new Date(station.lastUpdated).toLocaleString()}</span>
                    <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded">Owner Code: {station.ownerCode}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Edit Modal */}
      {editingStation && (
        <EditStationModal
          station={editingStation}
          onClose={() => setEditingStation(null)}
        />
      )}
      
      <Footer />
    </div>
  );
};

export default OwnerPortal;
